/**
 * Resolves the entity a staff member is currently looking at from the URL.
 *
 * The assistant's system prompt already handles this ("the user is currently
 * viewing a client…, when they say 'this' they mean it") and the tools take ids —
 * but the widget only ever sent a pathname, so "why was this member charged twice?"
 * arrived with no idea which member. The provider sits above the router and cannot
 * use useParams, so the id is read from the path itself.
 */

export interface PageEntity {
  entityType: string
  entityId: string
}

/**
 * Each pattern captures the id in group 1. Ordered most specific first, since a
 * nested route would otherwise match its parent.
 */
const ENTITY_PATTERNS: Array<{ pattern: RegExp; entityType: string }> = [
  { pattern: /\/clients\/([^/?#]+)/, entityType: 'client' },
  { pattern: /\/retail\/invoice\/([^/?#]+)/, entityType: 'invoice' },
  { pattern: /\/retail\/recurring-payment\/([^/?#]+)/, entityType: 'recurring payment' },
  { pattern: /\/content-suite\/([^/?#]+)/, entityType: 'content entry' },
  { pattern: /\/control-center\/onboarding\/([^/?#]+)/, entityType: 'migration' },
  { pattern: /\/control-center\/marketplace\/([^/?#]+)/, entityType: 'marketplace provider' },
]

/**
 * Path segments that look like an id but are really a sub-page, so a list or tab
 * route is not reported as a specific record.
 */
const NOT_AN_ID = new Set([
  'new',
  'create',
  'add',
  'list',
  'all',
  'import',
  'export',
  'settings',
  'edit',
])

export function resolvePageEntity(pathname: string): PageEntity | undefined {
  for (const { pattern, entityType } of ENTITY_PATTERNS) {
    const entityId = pattern.exec(pathname)?.[1]
    if (entityId && !NOT_AN_ID.has(entityId.toLowerCase())) {
      return { entityType, entityId }
    }
  }
  return undefined
}
